import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useMultiCmt } from '../contexts/MultiCmtContext';
import { useFirestore } from '../contexts/FirestoreContext';
import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import PageHeader from '../components/PageHeader';
import { DIGITAL_PRODUCTS } from '../models/digitalProducts';
import { ROLES } from '../models/role';
import PlanModal from '../components/plans/PlanModal';

export default function MyCrisisResources() {
  const { currentUser } = useAuth();
  const { db } = useFirestore();
  const navigate = useNavigate();
  const { activeCmt } = useMultiCmt();
  const [error, setError] = useState('');
  const [digitalProducts, setDigitalProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Plan editing state
  const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
  const [selectedPlanKey, setSelectedPlanKey] = useState(null);

  // Check if user has edit permissions
  const canEditProducts = [
    ROLES.CMT_LEADER,
    ROLES.CMT_LEADER_ALT,
    ROLES.CMT_ADMIN
  ].includes(currentUser?.role);

  // Check if user is super admin
  const isSuperAdmin = currentUser?.isSuperAdmin;

  // 🎯 REMOVED: IN_APP_PLANS constant - functionality moved to digital products table

  useEffect(() => {
    const fetchDigitalProducts = async () => {
      try {
        setLoading(true);
        console.log('=== DEBUG: MyCrisisResources ===');
        console.log('Current user:', {
          uid: currentUser.uid,
          orgId: currentUser.orgId,
          email: currentUser.email,
          role: currentUser.role,
          canEdit: canEditProducts,
          isSuperAdmin
        });

        // If super admin, return all products
        if (isSuperAdmin) {
          console.log('Super admin detected, returning all products');
          const allProducts = Object.values(DIGITAL_PRODUCTS)
            .sort((a, b) => a.name.localeCompare(b.name));
          setDigitalProducts(allProducts);
          setLoading(false);
          return;
        }
        
        // For non-super admin users, get organization's products
        const orgRef = doc(db, 'c3_organisations', currentUser.orgId);
        const orgDoc = await getDoc(orgRef);
        const orgData = orgDoc.data();
        console.log('Organization data:', orgData);
        console.log('Organization digitalProducts array:', orgData?.digitalProducts);
        
        // Get the organization's product mappings
        const orgProductsRef = collection(db, 'c3_organisation_products');
        const orgProductsQuery = query(
          orgProductsRef,
          where('organisationId', '==', currentUser.orgId)
        );
        console.log('Querying org products with orgId:', currentUser.orgId);
        
        const orgProductsSnapshot = await getDocs(orgProductsQuery);
        const orgProductsDocs = orgProductsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        console.log('Raw org products docs:', orgProductsDocs);
        
        // Get the list of product IDs from both sources
        const orgProductIds = orgData?.digitalProducts || [];
        const mappedProductIds = orgProductsDocs.map(doc => doc.productId);
        
        console.log('Product IDs from org:', orgProductIds);
        console.log('Product IDs from mappings:', mappedProductIds);
        
        // Combine both sets of product IDs
        const accessibleProductIds = [...new Set([...orgProductIds, ...mappedProductIds])];
        console.log('Combined accessible product IDs:', accessibleProductIds);
        
        // Log all available DIGITAL_PRODUCTS
        console.log('All available DIGITAL_PRODUCTS:', Object.values(DIGITAL_PRODUCTS).map(p => ({
          id: p.id,
          name: p.name
        })));
        
        // Filter the DIGITAL_PRODUCTS object to only include products the org has access to
        const accessibleProducts = Object.values(DIGITAL_PRODUCTS)
          .filter(product => {
            const hasAccess = accessibleProductIds.includes(product.id);
            console.log(`Product ${product.name} (${product.id}) - Has access: ${hasAccess}`);
            return hasAccess;
          })
          .sort((a, b) => a.name.localeCompare(b.name));

        console.log('Final filtered products:', accessibleProducts.map(p => ({
          id: p.id,
          name: p.name
        })));
        
        setDigitalProducts(accessibleProducts);
      } catch (err) {
        console.error('Error loading digital products:', err);
        console.error('Error details:', {
          message: err.message,
          code: err.code,
          stack: err.stack
        });
        setError('Failed to load digital products');
      } finally {
        setLoading(false);
      }
    };

    fetchDigitalProducts();
  }, [db, currentUser, canEditProducts, isSuperAdmin]);

  const handleViewDetails = (productId) => {
    console.log('View Details clicked for product:', productId);
    
    // 🎯 CHECK: If this is Crisis Communication Plan, use dedicated viewer
    if (productId === 'crisis_communication_plan') {
      console.log('🎯 Crisis Communication Plan detected - using dedicated viewer');
      
      if (!activeCmt) {
        setError('Please select a CMT to view Crisis Communication Plan');
        return;
      }
      
      // Navigate to dedicated Crisis Communication Plan viewer
      const resolvedCmtId = activeCmt?.id;
      const path = `/crisis-communication-plan/${resolvedCmtId}`;
      console.log('Navigating to Crisis Communication Plan:', path);
      navigate(path);
      return;
    }

    // 🎯 CHECK: If this is Business Continuity Plan, use dedicated viewer
    if (productId === 'business_continuity_plan') {
      console.log('🎯 Business Continuity Plan detected - using dedicated viewer');
      
      if (!activeCmt) {
        setError('Please select a CMT to view Business Continuity Plan');
        return;
      }
      
      // Navigate to dedicated Business Continuity Plan viewer
      const resolvedCmtId = activeCmt?.id;
      const path = `/business-continuity-plan/${resolvedCmtId}`;
      console.log('Navigating to Business Continuity Plan:', path);
      navigate(path);
      return;
    }
    
    // 🎯 CHECK: If this is Crisis Plan, use in-app modal editor
    if (productId === 'crisis_plan') {
      console.log('🎯 Crisis Plan detected - using in-app editor');
      
      if (!activeCmt) {
        setError('Please select a CMT to edit Crisis Plan');
        return;
      }
      
      // Use the in-app plan editor for Crisis Plan
      handleEditPlan(productId);
      return;
    }
    
    // For all other products, use the regular DigitalProductViewer
    // For client view, include the CMT ID so the viewer can load the most recent version
    // Super admin should be able to view any product without CMT context
    const resolvedCmtId = activeCmt?.id;
    const path = isSuperAdmin 
      ? `/digital-products/${productId}` 
      : `/digital-products/${productId}/${resolvedCmtId}`;
    console.log('Navigating to:', path);
    navigate(path);
  };

  // Plan editing handlers
  const handleEditPlan = (planKey) => {
    if (!activeCmt) {
      setError('Please select a CMT to edit plans');
      return;
    }
    setSelectedPlanKey(planKey);
    setIsPlanModalOpen(true);
  };

  const handleClosePlanModal = () => {
    setIsPlanModalOpen(false);
    setSelectedPlanKey(null);
  };

  if (loading) {
    return (
      <div className="p-4">
        <PageHeader title="My Crisis Resources" />
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <PageHeader title="My Crisis Resources" />

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {/* CMT Selection Notice */}
      {!activeCmt && (
        <div className="alert alert-warning" role="alert">
          <h5 className="alert-heading">
            <i className="fas fa-exclamation-triangle me-2"></i>
            Select a Crisis Management Team
          </h5>
          <p className="mb-0">
            Please select a CMT from the sidebar to access and edit your crisis plans. 
            Plan editing is isolated per CMT to ensure security and proper governance.
          </p>
        </div>
      )}

      {/* 🎯 REMOVED: In-App Editable Plans Section - Now integrated into Digital Products table */}

      {/* Digital Products Section */}
      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <h2 className="h4 mb-4">
            {canEditProducts 
              ? "This view shows the CompassCrisis crisis and resilience resources that your organisation has access to. You can view and edit these resources. Crisis Plan and Crisis Communication Plan support in-app editing with real-time autosave."
              : "This view shows the CompassCrisis crisis and resilience resources that your organisation has access to. You can view these resources. Crisis Plan and Crisis Communication Plan support in-app viewing."}
          </h2>
          <div className="table-responsive">
            <table className="table table-striped">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Description</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {digitalProducts.map((product) => (
                  <tr key={product.id}>
                    <td>{product.name}</td>
                    <td>{product.description}</td>
                    <td>
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => handleViewDetails(product.id)}
                        style={{ borderColor: '#39325b', color: '#39325b' }}
                      >
                        {/* 🎯 Show different text for in-app editable plans */}
                        {(product.id === 'crisis_plan' || product.id === 'crisis_communication_plan' || product.id === 'business_continuity_plan') 
                          ? (canEditProducts ? 'Edit in App' : 'View in App')
                          : (canEditProducts ? 'View and Edit' : 'View')
                        }
                      </button>
                    </td>
                  </tr>
                ))}
                {digitalProducts.length === 0 && (
                  <tr>
                    <td colSpan="3" className="text-center py-4">
                      No digital products available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Plan Editor Modal */}
      <PlanModal
        open={isPlanModalOpen}
        onClose={handleClosePlanModal}
        planKey={selectedPlanKey}
      />
    </div>
  );
} 